<script setup lang="ts">
import { computed, defineAsyncComponent, h, nextTick, onMounted, ref, watch } from 'vue'
import { NAvatar, NButton, NDropdown, NLayout, NLayoutContent, NLayoutHeader, NModal, NText, NTooltip } from 'naive-ui'
// import { useRouter } from 'vue-router'
import { useRouter } from 'vue-router'
import { lighten, rgba } from 'polished'
import Permission from './Permission.vue'
import Sider from './sider/index.vue'
import { useBasicLayout } from '@/hooks/useBasicLayout'
import { useAppStore, useAuthStore, useCreditStore, useUserStore } from '@/store'
import { LottieAnimation, SvgIcon } from '@/components/common'
import CreditIcon from '@/components/common/CreditIcon/index.vue'
import defaultAvatar from '@/assets/avatar.jpg'
import { useWorkspaceFolderStore } from '@/store/modules/folder'
import { t } from '@/locales'
import { getUserInfo } from '@/api/users'
import { transformMinioUrl } from '@/utils/functions'
import Breadcrumb from '@/components/common/Breadcrumb/index.vue'
import { RolePermission, hasPermission, isUserRole } from '@/constants/role'
import CreditDisplay from '@/components/common/CreditDisplay.vue'
import welcomeBonus from '@/assets/lottie-files/billing/wcbonus.json'
import FormRequestWorkspace from '@/views/settings/user-management/FormRequestWorkspace.vue'
import { getApprovedAccount } from '@/api/workspace'

// Router and store initialization
// const router = useRouter()
const userStore = useUserStore()
const appStore = useAppStore()
const userRole = computed(() => userStore.role)
// const chatStore = useChatStore()
const authStore = useAuthStore()
const creditStore = useCreditStore()
const router = useRouter()

// Get mobile detection hook
const { isMobile } = useBasicLayout()
const workspaceFolderStore = useWorkspaceFolderStore()

// Welcome bonus modal state
const showWelcomeBonusModal = ref(false)

// Collapsed state from the app store
const collapsed = computed(() => appStore.siderCollapsed)

// Permission check based on authentication
const needPermission = computed(() => !!authStore.session?.auth && !authStore.token)

// Mobile class computation
const getMobileClass = computed(() => {
  return isMobile.value
    ? ['rounded-none', 'shadow-none']
    : ['border', 'rounded-md', 'shadow-md', 'dark:border-neutral-800']
})

const listWorkSpaceFolder = computed(() => workspaceFolderStore.getFolders)
const show = ref(false)
const Setting = defineAsyncComponent(() => import('@/components/common/Setting/index.vue'))
const userProfile = ref<any>(null)

// Dynamic color system based on primary color
const primaryColor = computed(() => appStore.colorTheme || '#3b82f6')
const primaryColorLight = computed(() => lighten(0.1, primaryColor.value))
const primaryColorRgba = computed(() => rgba(primaryColor.value, 0.1))

// Container class computation based on mobile and collapsed state
const getContainerClass = computed(() => {
  return [
    'min-h-screen',
    (!isMobile.value && !collapsed.value) ? 'pl-[260px]' : 'pl-[70px]',
  ]
})

const basicCredit = computed(() => creditStore.basicCreditBalance)
const advancedCredit = computed(() => creditStore.advancedCreditBalance)
const showRequestWorkspace = computed(() => userStore.showRequestWorkspace)
const workSpaceId = computed(() => userStore.workSpaceId)
const userInfo = computed(() => userStore.userInfo)
const hasRequestData = ref<boolean>(false)

const handleLogout = async () => {
  await authStore.logout()
  // await router.push({ name: 'login' })
}

const renderCustomHeader = () => {
  return h(
    'div',
    {
      style: 'display: flex; align-items: center; padding: 8px 12px;',
    },
    [
      h(NAvatar, {
        round: true,
        style: 'margin-right: 12px;',
        src: userInfo?.value?.icon_url ? transformMinioUrl(userInfo?.value?.icon_url) : defaultAvatar,
      }),
      h('div', null, [
        h('div', null, [
          h(NText, { depth: 2 }, { default: () => userInfo?.value?.fullName }),
        ]),
        h('div', { style: 'font-size: 12px;' }, [
          h(NText, { depth: 3 }, { default: () => userInfo?.value?.email }),
        ]),
      ]),
    ],
  )
}

const renderSettingsHeader = () => {
  return h(
    'div',
    {
      style: 'display: flex; align-items: center; padding: 8px 12px;',
    },
    [
      h('div', null, [
        h('div', null, [
          h(NText, { depth: 2, class: 'text-lg font-semibold' }, { default: () => t('settings.workspaceSettings.title') }),
        ]),
      ]),
    ],
  )
}

// Settings options
const settingsOptions = computed(() => [
  {
    key: 'header',
    type: 'render',
    render: renderSettingsHeader,
  },
  {
    type: 'divider',
    key: 'd1',
  },
  // {
  //   label: t('settings.workspaceSettings.systemSettings'),
  //   key: 'systemSettings',
  //   icon: () => h(SvgIcon, { icon: 'ion:shield-half-outline', size: '18' }),
  //   show: userStore.role?.code === 'super_admin',
  // },
  {
    label: t('settings.workspaceSettings.workspaceInfo'),
    key: 'workspaceInfo',
    icon: () => h(SvgIcon, { icon: 'ion:business-outline', size: '18' }),
  },
  // {
  //   label: t('settings.workspaceSettings.workspaceApprove'),
  //   key: 'workspaceApprove',
  //   icon: () => h(SvgIcon, { icon: 'ion:checkmark-circle-outline', size: '18' }),
  //   show: userStore.role?.code === 'super_admin',
  // },
  // {
  //   label: t('settings.workspaceSettings.imageApprove'),
  //   key: 'imageApprove',
  //   icon: () => h(SvgIcon, { icon: 'ion:checkmark-circle-outline', size: '18' }),
  //   show: userStore.role?.code === 'super_admin',
  // },
  // {
  //   label: t('settings.workspaceSettings.aiModel'),
  //   key: 'aiModel',
  //   icon: () => h(SvgIcon, { icon: 'ri:robot-line', size: '18' }),
  //   show: userStore.role?.code === 'super_admin',
  // },
  {
    label: t('settings.workspaceSettings.activity'),
    key: 'activity',
    icon: () => h(SvgIcon, { icon: 'ion:time-outline', size: '18' }),
  },
  {
    label: t('settings.workspaceSettings.notification'),
    key: 'notification-config',
    icon: () => h(SvgIcon, { icon: 'ion:notifications-outline', size: '18' }),
  },
  {
    label: t('settings.workspaceSettings.userManagement'),
    key: 'userManagement',
    icon: () => h(SvgIcon, { icon: 'ion:people-outline', size: '18' }),
  },
  {
    label: t('settings.workspaceSettings.roleManagement'),
    key: 'roleManagement',
    icon: () => h(SvgIcon, { icon: 'ion:people-circle-outline', size: '18' }),
  },
  // {
  //   label: t('settings.workspaceSettings.parameterConfiguration'),
  //   key: 'parameterConfiguration',
  //   icon: () => h(SvgIcon, { icon: 'ion:grid-outline', size: '18' }),
  //   show: userStore.role?.code === 'super_admin',
  // },
  // {
  //   label: t('settings.workspaceSettings.apiServiceConfiguration'),
  //   key: 'apiServiceConfiguration',
  //   icon: () => h(SvgIcon, { icon: 'ion:server-outline', size: '18' }),
  //   show: userStore.role?.code === 'super_admin',
  //
  // },
  {
    label: t('settings.workspaceSettings.interface'),
    key: 'interface',
    icon: () => h(SvgIcon, { icon: 'ion:color-palette-outline', size: '18' }),
  },
])

// Dropdown options
const dropdownOptions = computed(() => [
  {
    key: 'header',
    type: 'render',
    render: renderCustomHeader,
  },
  {
    label: t('settings.userManagementDropdown.userInfo'),
    key: 'profile',
    icon: () => h(SvgIcon, { icon: 'ion:person-outline', size: '18' }),
  },
  {
    label: t('settings.userManagementDropdown.securitySettings'),
    key: 'security',
    icon: () => h(SvgIcon, { icon: 'ion:shield-checkmark-outline', size: '18' }),
  },
  {
    type: 'divider',
    key: 'd1',
  },
  {
    label: t('settings.userManagementDropdown.logout'),
    key: 'logout',
    icon: () => h(SvgIcon, { icon: 'mi:log-out', size: '18' }),
  },
])

const handleViewProfile = () => {
  router.push({ name: 'user-profile' })
}

const handleViewWorkspaceInfo = () => {
  router.push({ name: 'workspace-settings', params: { tab: 'workspace-info' } })
}
const handleViewWorkspaceApprove = () => {
  router.push({ name: 'workspace-approve', query: { tab: 'pending' } })
}

const handleViewActivity = () => {
  router.push({ name: 'workspace-settings', params: { tab: 'activity' } })
}

const handleViewUserManagement = () => {
  router.push({ name: 'workspace-settings', params: { tab: 'user-management' } })
}

const handleViewRoleManagement = () => {
  router.push({ name: 'workspace-settings', params: { tab: 'role-management' } })
}

const handleViewInterface = () => {
  show.value = true
}

const handleConfigParam = () => {
  router.push({ name: 'parameter-configuration' })
}

const handleViewApiServiceConfiguration = () => {
  router.push({ name: 'api-service-configuration' })
}

const handleWelcomeBonusClose = () => {
  showWelcomeBonusModal.value = false
  creditStore.resetIsCreatedWallet()
}

const getDetailUserInfo = async () => {
  try {
    const res = await getUserInfo({
      user_id: userInfo.value?.userId || '',
    })
    if (res?.success && res.data) {
      userProfile.value = res.data
      const update = {
        fullName: res.data.fullName || null,
        address: res.data.address || null,
        icon_url: res.data.icon_url || null,
        icon: res.data.icon || null,
        position: res.data.position || null,
        sdt: res.data.sdt || null,
      }
      userStore.updateUserInfo(update)
    }

    // userStore.workSpaceLogo = res.data.icon_url
  }
  catch (error) {
    console.error('Error fetching user info:', error)
  }
}

// Handle dropdown selection
const handleSelectSettings = (key: string) => {
  switch (key) {
    case 'systemSettings':
      handleSystemSettings()
      break
    case 'workspaceInfo':
      handleViewWorkspaceInfo()
      break
    case 'workspaceApprove':
      handleViewWorkspaceApprove()
      break
    case 'activity':
      handleViewActivity()
      break
    case 'userManagement':
      handleViewUserManagement()
      break
    case 'roleManagement':
      handleViewRoleManagement()
      break

    case 'apiServiceConfiguration':
      handleViewApiServiceConfiguration()
      break
    case 'interface':
      handleViewInterface()
      break
    case 'imageApprove':
      handleViewImageApprove()
      break
    case 'aiModel':
      handleViewAIModel()
      break
    case 'notification-config':
      handleViewNotificationConfig()
      break
    default:
      handleConfigParam()
  }
}

// Handle dropdown selection
const handleSelect = (key: string) => {
  switch (key) {
    case 'profile':
      handleViewProfile()
      break
    case 'security':
      router.push({ name: 'security-settings' })
      break
    case 'logout':
      handleLogout()
      break
  }
}

const handleSwap = async () => {
  const type = userStore?.type || null
  if (type)
    await authStore.logout()

  else
    router.push({ name: 'Workspace' })
}

const handleTitle = (title: any, name: any, route: any) => {
  if (name === 'folder') {
    const obj = listWorkSpaceFolder.value.find(item => String(item.id) === String(route.params.folderId))
    // eslint-disable-next-line no-mixed-operators
    return obj && obj.name || ''
  }
  return title
}

const checkRequestData = async () => {
  if (!workSpaceId.value || !showRequestWorkspace.value) {
    hasRequestData.value = false
    return
  }
  try {
    const { data } = await getApprovedAccount({ workspace_id: workSpaceId.value })
    if (data.success) {
      const items = data?.data?.items || []
      hasRequestData.value = items.length > 0
    }
    else {
      hasRequestData.value = false
    }
  }
  catch (error) {
    hasRequestData.value = false
  }
  finally {
  }
}

const handleViewImageApprove = () => {
  router.push({ name: 'image-approve' })
}

const handleViewAIModel = () => {
  router.push({ name: 'ai-model' })
}

const handleSystemSettings = () => {
  router.push({ name: 'system-settings' })
}

const handleViewNotificationConfig = () => {
  router.push({ name: 'notification-config' })
}

const handleRedirect = () => {
  if (!workSpaceId.value) {
    if (listWorkSpaceFolder.value.length === 0) {
      if (isUserRole(userRole.value, 'ADMIN', 'TEACHER'))
        router.push({ name: 'space' })

      else if (isUserRole(userRole.value, 'STUDENT'))
        router.push({ name: 'space-student' })
    }

    else { router.push({ name: 'Workspace' }) }
  }
  else {
    if (isUserRole(userRole.value, 'STUDENT'))
      router.push({ name: 'classroom-v2' })
  }
}

// Watch credit changes for animation triggers
watch(basicCredit, (newVal, oldVal) => {
  // Credit animation is handled by CreditDisplay component
})

watch(workSpaceId, () => {
  handleRedirect()
})
watch(userRole, () => {
  handleRedirect()
})

onMounted(() => {
  handleRedirect()
  getDetailUserInfo()
  // Fetch credit data and billing configs when component mounts
  const token = authStore.token
  if (token) {
    Promise.all([
      creditStore.fetchCreditData(),
      creditStore.fetchBillingConfigs(),
    ]).then(() => {
    // Check if this is first time login and show welcome bonus
      if (!creditStore.isWalletCreated) {
      // Use nextTick to ensure component is fully mounted
        nextTick(() => {
          showWelcomeBonusModal.value = true
        })
        creditStore.resetIsCreatedWallet()
      }
    })
  }
  checkRequestData()
})
</script>

<template>
  <div
    class="min-h-screen dark:bg-gradient-to-br dark:from-[#1a1d29] dark:to-[#2d3348] bg-gradient-to-br from-gray-50 to-blue-50 transition-all duration-300 p-0"
  >
    <div class="min-h-screen relative z-10" :class="getMobileClass">
      <NLayout has-sider class="z-40 transition-all duration-300" :class="getContainerClass">
        <Sider />
        <NLayout>
          <NLayoutHeader class="header-glassmorphism">
            <div
              class="flex items-center justify-between backdrop-blur-xl bg-white/80 dark:bg-gray-900/50 border-b border-white/20 dark:border-gray-700/50 h-auto min-h-16 shadow-lg shadow-blue-500/5 dark:shadow-purple-500/10 flex-wrap-reverse"
            >
              <!-- Breadcrumb Navigation -->
              <div class="breadcrumb-wrapper">
                <Breadcrumb />
              </div>

              <!-- Enhanced Header Actions -->
              <div class="flex items-center justify-between px-4 py-3 gap-3 ml-auto">
                <!-- Credit Stats with Basic and Advanced -->
                <!-- Combined Credit Display -->
                <CreditDisplay :basic-credit="basicCredit" :advanced-credit="advancedCredit" />

                <div class="h-6 w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent dark:via-gray-600" />

                <NTooltip trigger="hover">
                  <template #trigger>
                    <div class="action-button-modern relative" :style="{ '--primary-color': primaryColor }">
                      <SvgIcon icon="ion:notifications-outline" size="18" />
                      <!-- Notification dot -->
                      <div class="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-ping" />
                      <div class="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                    </div>
                  </template>
                  {{ t('common.notifications') }}
                </NTooltip>

                <div class="h-6 w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent dark:via-gray-600" />

                <!-- Settings Dropdown -->
                <NDropdown
                  v-if="userStore.role?.is_owner === true "
                  trigger="hover" :options="settingsOptions" placement="bottom-end"
                  @select="handleSelectSettings"
                >
                  <div class="action-button-modern" :style="{ '--primary-color': primaryColor }">
                    <SvgIcon icon="ion:settings-outline" size="18" class="animate-spin-slow" />
                  </div>
                </NDropdown>
                <NTooltip v-else trigger="click">
                  <template #trigger>
                    <div class="action-button-modern relative" :style="{ '--primary-color': primaryColor }" @click="handleViewInterface">
                      <SvgIcon icon="ion:color-palette-outline" size="18" />
                    </div>
                  </template>
                  {{ t('settings.workspaceSettings.interface') }}
                </NTooltip>
                <NTooltip
                  v-if="userStore.role && isUserRole(userStore.role, 'ADMIN') && userStore.role?.is_owner === false"
                  trigger="click"
                >
                  <template #trigger>
                    <div class="action-button-modern" :style="{ '--primary-color': primaryColor }" @click="handleViewUserManagement">
                      <SvgIcon icon="ion:people-outline" size="18" />
                    </div>
                  </template>
                  {{ t('settings.workspaceSettings.userManagement') }}
                </NTooltip>
                <div
                  v-if="userStore.role?.is_owner === true || hasPermission(userStore.role, RolePermission.WORKSPACE_VIEW)"
                  class="h-6 w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent dark:via-gray-600"
                />

                <!-- User Profile Dropdown with dynamic gradient -->
                <NDropdown trigger="hover" :options="dropdownOptions" placement="bottom-end" @select="handleSelect">
                  <div class="profile-avatar-container">
                    <div class="relative">
                      <div
                        class="w-9 h-9 rounded-full p-0.5 hover:scale-110 transition-all duration-300 shadow-lg"
                        :style="{ background: `linear-gradient(to top right, ${primaryColor}, ${primaryColorLight})` }"
                      >
                        <img
                          :src="transformMinioUrl(userProfile?.icon_url) || defaultAvatar" alt="User Avatar"
                          class="w-full h-full rounded-full object-cover border-2 border-white dark:border-gray-800"
                        >
                      </div>
                    </div>
                  </div>
                </NDropdown>

                <!-- Workspace Swap Button -->
                <NTooltip trigger="hover">
                  <template #trigger>
                    <div
                      class="action-button-modern" :style="{ '--primary-color': primaryColor }"
                      @click.stop="handleSwap"
                    >
                      <SvgIcon
                        icon="ion:repeat-outline" size="18"
                        class="hover:rotate-180 transition-transform duration-500"
                      />
                    </div>
                  </template>
                  {{ t('common.swapWorkspace') }}
                </NTooltip>
              </div>
            </div>
          </NLayoutHeader>

          <!-- Enhanced Content Area -->
          <NLayoutContent class="content-area">
            <div class="page-container">
              <RouterView v-slot="{ Component, route: routerRoute }">
                <template v-if="Component">
                  <Transition name="page-transition" mode="out-in" appear>
                    <div :key="routerRoute.path" class="route-wrapper">
                      <Suspense>
                        <component :is="Component" />
                      </Suspense>
                    </div>
                  </Transition>
                </template>
              </RouterView>
            </div>
          </NLayoutContent>
        </NLayout>
      </NLayout>
    </div>

    <Permission :visible="needPermission" />
  </div>
  <Setting v-if="show" v-model:visible="show" />

  <!-- Welcome Bonus Modal -->
  <NModal
    v-model:show="showWelcomeBonusModal"
    preset="card"
    style="max-width: 550px;"
    class="!rounded-2xl "
    :mask-closable="true"
    :closable="false"
  >
    <div class="text-center pb-4">
      <div
        class="w-48 h-48 rounded-full flex items-center justify-center mx-auto"
      >
        <LottieAnimation
          :animation-data="welcomeBonus"
          :loop="true"
          :autoplay="true"
          :speed="1"
          :width="400"
          :height="400"
        />
      </div>
      <!-- Title with gradient text -->
      <h2 class="text-3xl font-bold mb-4 shimmer-text">
        {{ t('workspace.welcomeBonus') }}
      </h2>

      <!-- Description -->
      <p class="text-base text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
        {{ t('workspace.welcomeBonusDescription') }}
      </p>

      <!-- Credit Amount Display with Naive UI components -->
      <div class="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-6 mb-6 border border-blue-200/50 dark:border-blue-700/30 shadow-lg">
        <div class="flex flex-col gap-4">
          <!-- Basic Credits -->
          <div class="flex items-center justify-center gap-3 p-3 bg-white/60 dark:bg-gray-800/60 rounded-lg border border-blue-200/50 dark:border-blue-700/30 backdrop-blur-sm">
            <div class="relative">
              <CreditIcon type="basic" :size="24" />
            </div>
            <span class="text-lg font-bold text-gray-600 dark:text-gray-400">
              {{ t('workspace.welcomeBonusBasic') }}
            </span>
          </div>

          <!-- Advanced Credits -->
          <div class="flex items-center justify-center gap-3 p-3 bg-white/60 dark:bg-gray-800/60 rounded-lg border border-purple-200/50 dark:border-purple-700/30 backdrop-blur-sm">
            <div class="relative">
              <CreditIcon type="advanced" :size="24" />
            </div>
            <span class="text-lg font-bold text-purple-600 dark:text-purple-400">
              {{ t('workspace.welcomeBonusAdvanced') }}
            </span>
          </div>
        </div>
      </div>

      <!-- Sub description with better styling -->
      <!-- <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
        {{ t('workspace.welcomeBonusSubDescription') }}
      </p> -->
    </div>

    <div class="text-center pb-4">
      <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
        {{ t('workspace.welcomeBonusUpgrade') }}
      </p>
    </div>

    <NButton
      type="primary"
      class="w-full"
      size="large"
      :style="{
        background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
        border: 'none',
        width: '100%',
      }"
      @click="handleWelcomeBonusClose"
    >
      <template #icon>
        <SvgIcon icon="ri:rocket-line" size="16" />
      </template>
      {{ t('creditDisplay.upgrade') }}
    </NButton>
  </NModal>
  <FormRequestWorkspace
    v-if="showRequestWorkspace && hasRequestData"
    :show="showRequestWorkspace && hasRequestData"
    :workspace-id="workSpaceId"
    @on-close="(reload) => { userStore.setShowRequestWorkspace(false) }"
  />
</template>

<style scoped>
/* Gradient Animation */
@keyframes gradient-x {
  0%,
  100% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }
}

.animate-gradient-x {
  background-size: 200% 200%;
  animation: gradient-x 3s ease infinite;
}

/* Slow Spin Animation */
@keyframes spin-slow {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.animate-spin-slow {
  animation: spin-slow 8s linear infinite;
}

/* Header Glassmorphism */
.header-glassmorphism {
  position: relative;
  z-index: 50;
}

.header-glassmorphism::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  backdrop-filter: blur(20px);
  border-radius: 0;
  z-index: -1;
}

/* Modern Action Buttons with dynamic colors */
.action-button-modern {
  @apply relative flex items-center justify-center w-9 h-9 rounded-2xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 text-gray-600 dark:text-gray-300 transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md hover:scale-105;
}

.action-button-modern:hover {
  color: var(--primary-color);
  background: rgba(255, 255, 255, 0.9);
}

.dark .action-button-modern:hover {
  background: rgba(55, 65, 81, 0.9);
}

.action-button-modern:hover::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, var(--primary-color), rgba(147, 51, 234, 0.1));
  opacity: 0.1;
  border-radius: 0.75rem;
  z-index: -1;
}

/* Profile Avatar Container with dynamic colors */
.profile-avatar-container {
  @apply cursor-pointer relative;
}

.profile-avatar-container:hover::before {
  content: '';
  position: absolute;
  inset: -4px;
  background: conic-gradient(from 180deg, var(--primary-color), transparent, var(--primary-color));
  border-radius: 50%;
  z-index: -1;
  animation: pulse 2s infinite;
  opacity: 0.3;
}

/* Breadcrumb Wrapper Styles */
.breadcrumb-wrapper {
  @apply px-3 py-3;
}

/* Content and Page Container */
.content-area {
  /*@apply h-full overflow-hidden;*/
}

/* Content Area with dynamic gradients */
.content-area {
	/*height: calc(100vh - 64px);*/
  overflow: auto;
  position: relative;
	display: flex;
	flex-direction: column;
	flex: 1 1 0%;
}

/* Content Area with dynamic gradients - full screen */
.content-area::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 20% 20%, v-bind('primaryColorRgba') 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, v-bind('rgba(primaryColorLight, 0.1)') 0%, transparent 50%),
    radial-gradient(circle at 40% 60%, rgba(16, 185, 129, 0.05) 0%, transparent 50%);
  pointer-events: none;
  z-index: 0;
}

.content-area>div {
  position: relative;
  z-index: 1;
}

/* Page Container - ensures single root element for transitions */
.page-container {
  width: 100%;
  height: 100%;
  min-height: 100%;
  position: relative;
}

.route-wrapper {
  width: 100%;
  height: 100%;
  min-height: 100%;
  display: block;
  position: relative;
  overflow: auto;
}

/* Ensure child components take full space */
.route-wrapper>* {
  width: 100%;
  /* min-height: 100%; */
}

/* Layout Transitions */
:deep(.n-layout .n-layout-scroll-container) {
  /*height: auto;*/
	display: flex;
	flex-direction: column;
}

/* Page Transitions - Simple fade and slide */
.page-transition-enter-active {
  transition: all 0.3s ease-out;
}

.page-transition-leave-active {
  transition: all 0.2s ease-in;
}

.page-transition-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.page-transition-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.page-transition-enter-to,
.page-transition-leave-from {
  opacity: 1;
  transform: translateX(0);
}

/* Enhanced Scrollbar with dynamic colors */
.content-area::-webkit-scrollbar {
  width: 6px;
}

.content-area::-webkit-scrollbar-track {
  background: transparent;
}

.content-area::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, v-bind('rgba(primaryColor, 0.3)'), v-bind('rgba(primaryColorLight, 0.3)'));
  border-radius: 3px;
}

.content-area::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, v-bind('rgba(primaryColor, 0.5)'), v-bind('rgba(primaryColorLight, 0.5)'));
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .action-button-modern {
    @apply w-8 h-8;
  }

  .profile-avatar-container .w-10 {
    @apply w-8 h-8;
  }
}

/* Shimmer Text Effect */
.shimmer-text {
  background: linear-gradient(
    90deg,
    #3b82f6 0%,
    #8b5cf6 25%,
    #ec4899 50%,
    #8b5cf6 75%,
    #3b82f6 100%
  );
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: shimmer 3s ease-in-out infinite;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.dark .shimmer-text {
  background: linear-gradient(
    90deg,
    #60a5fa 0%,
    #a78bfa 25%,
    #f472b6 50%,
    #a78bfa 75%,
    #60a5fa 100%
  );
  background-size: 200% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
</style>
